import { useEffect, useState } from "react";
import axiosInstance from "../../services/axios";
import { FormDto, Category } from "../../types/category.types";
import styles from "../css/ChildForms.module.css";
import { baseURL } from "../../services/axios";


interface ChildFormsAnalystProps {
  childId: number;
  childName: string;
}

export const ChildFormsAnalyst = ({ childId, childName }: ChildFormsAnalystProps) => {
  const [forms, setForms] = useState<FormDto[]>([]);
  const [filteredForms, setFilteredForms] = useState<FormDto[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<number | "">("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!isMounted) return;
      try {
        setLoading(true);
        const [formsRes, categoriesRes] = await Promise.all([
          axiosInstance.get(`/Child/${childId}/forms`),
          axiosInstance.get(`/FormCategory`),
        ]);

        if (!isMounted) return;

        setCategories(categoriesRes.data);
        setForms(
          formsRes.data.map((form: any) => {
            // תמיכה בשמות שדה שונים מהשרת
            let formCategoryId = form.formCategoryId ?? form.formCategoryCode ?? form.FormCategoryCode;
            if (typeof formCategoryId === "string") formCategoryId = Number(formCategoryId);
            const found = categoriesRes.data.find((c: Category) => c.code === formCategoryId);
            return { ...form, formCategoryId, categoryName: found?.categoryName || "" };
          })
        );
      } catch (error) {
        console.error("Error loading child forms", error);
        if (isMounted) {
          setError("שגיאה בטעינת הטפסים");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      setIsMounted(false);
    };
  }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps

  // סינון טפסים לפי קטגוריה
  useEffect(() => {
    if (selectedCategoryFilter === "") {
      setFilteredForms(forms);
    } else {
  setFilteredForms(forms.filter(form => form.FormCategoryId === selectedCategoryFilter));
    }
  }, [forms, selectedCategoryFilter]);

  if (loading) return <div className={styles.loading}>טוען טפסים...</div>;
  if (error) return <div className={styles.error}>שגיאה: {error}</div>;

  return (
    <div className={styles.container}>
      {/* סינון לפי קטגוריה */}
      <div className={styles.filterSection}>
        <div className={styles.filterContainer}>
          <label className={styles.filterLabel}>
            סינון לפי קטגוריה:
          </label>
          <select
            value={selectedCategoryFilter}
            onChange={e => setSelectedCategoryFilter(e.target.value === "" ? "" : Number(e.target.value))}
            className={styles.filterSelect}
          >
            <option value="">כל הקטגוריות</option>
            {categories.map((category) => (
              <option key={category.code} value={category.code}>
                {category.categoryName}
              </option>
            ))}
          </select>
          {selectedCategoryFilter !== "" && (
            <button
              onClick={() => setSelectedCategoryFilter("")}
              className={styles.clearFilterBtn}
            >
              נקה סינון
            </button>
          )}
        </div>
      </div>

      {/* טבלת הטפסים */}
      <div className={styles.tableContainer}>
        {filteredForms.length === 0 ? (
          <div className={styles.noData}>
            {forms.length === 0 ? "אין טפסים להצגה" : "לא נמצאו טפסים בקטגוריה זו"}
          </div>
        ) : (
          <table className={styles.formsTable}>
            <thead>
              <tr>
                <th>שם הטופס</th>
                <th>קטגוריה</th>
                <th>צפייה</th>
              </tr>
            </thead>
            <tbody>
              {filteredForms.map((f, i) => (
                <tr key={i}>
                  <td>{f.FileName}</td>
                  <td>{f.CategoryName || "ללא קטגוריה"}</td>
                  <td>
                    <a
                      href={`${baseURL}${f.FileUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.viewLink}
                    >
                      לצפייה בטופס
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
